import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'phosphor-react';
import { Button } from './Button';

interface EditCustomerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  phone: string;
  company?: string;
  contactName?: string;
  onSave: (company: string, contactName: string) => void;
}

export const EditCustomerDialog: React.FC<EditCustomerDialogProps> = ({
  isOpen,
  onClose,
  phone,
  company: initialCompany,
  contactName: initialContactName,
  onSave
}) => {
  const [company, setCompany] = useState(initialCompany || '');
  const [contactName, setContactName] = useState(initialContactName || '');

  // Update state when props change
  useEffect(() => {
    if (isOpen) {
      setCompany(initialCompany || '');
      setContactName(initialContactName || '');
    }
  }, [isOpen, initialCompany, initialContactName]);

  const handleSave = () => {
    onSave(company.trim(), contactName.trim());
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/20 z-50"
            onClick={onClose}
            onKeyDown={handleKeyDown}
          />
          
          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl px-6 py-6 border border-border shadow-lg max-w-md w-full">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-sans font-semibold text-lg text-text tracking-[-0.18px]">
                  Editar Cliente
                </h3>
                <button
                  onClick={onClose}
                  className="flex items-center justify-center p-2 rounded-md hover:bg-black/5 transition-colors"
                  aria-label="Cerrar"
                >
                  <X size={20} weight="bold" color="#2a2a2a" style={{ width: '20px', height: '20px', display: 'block', flexShrink: 0 }} />
                </button>
              </div>

              {/* Content */}
              <div className="flex flex-col gap-4 mb-6">
                {/* Phone (read-only) */}
                <div>
                  <label className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px] mb-2 block">
                    Número de teléfono
                  </label>
                  <input
                    type="text"
                    value={phone}
                    disabled
                    className="w-full rounded-xl px-4 py-3 font-sans font-normal text-base outline-none border border-border bg-gray-50 text-text-subtle cursor-not-allowed"
                  />
                </div>

                {/* Company */}
                <div>
                  <label className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px] mb-2 block">
                    Nombre de la empresa
                  </label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Nombre de la empresa"
                    className="w-full rounded-xl px-4 py-3 font-sans font-normal text-base outline-none border border-border focus:border-primary transition-colors"
                    autoFocus
                  />
                </div>

                {/* Contact Name */}
                <div>
                  <label className="font-sans font-medium text-sm text-text-subtle tracking-[-0.14px] mb-2 block">
                    Nombre de la persona
                  </label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Nombre de la persona"
                    className="w-full rounded-xl px-4 py-3 font-sans font-normal text-base outline-none border border-border focus:border-primary transition-colors"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 font-sans font-semibold text-sm text-text-subtle rounded-md hover:bg-black/5 transition-colors"
                >
                  Cancelar
                </button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                >
                  Guardar
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditCustomerDialog;

